import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Table,
} from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { useMutation, useQuery } from "react-query";
import queryString from "query-string";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";
import useDebounce, { useQueryParams } from "../../utils/hooks";
import { paginationOptions } from "../../utils/helpers";
import { useAuth } from "../../hooks/useAuth";
import { DeleteIcon, EditIcon } from "../Icons";
import ModalLoader from "../utils/ModalLoader";
import ConfirmDialog from "../ConfirmDialogue";
import NoTableItem from "../utils/NoTableItem";
import FormSelectorModal from "../CustomersEmail/FormSelectorModal";

const QUERY_KEY = "CERTIFICATION_CRUD";
const CERTIFICATION_TYPES_ENDPOINT = "/api/partnership/certification-types";

function normalizeCertificationItem(item = {}) {
  return {
    id: item.id ?? item.ID,
    name: item.name ?? item.Name ?? "",
    description: item.description ?? item.Description ?? "",
    icon_url: item.icon_url ?? item.iconUrl ?? item.IconUrl ?? "",
    formId: item.formId ?? item.form_id ?? item.FormID ?? null,
    created_at: item.created_at ?? item.createdAt ?? item.CreatedAt ?? null,
    updated_at: item.updated_at ?? item.updatedAt ?? item.UpdatedAt ?? null,
  };
}

function getErrorMessage(responsePayload) {
  return (
    responsePayload?.message ||
    responsePayload?.data?.message ||
    "Unable to perform action"
  );
}

function formatDate(value) {
  if (!value) return "---";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "---";
  return date.toLocaleString();
}

function CertificationModal({
  show,
  onHide,
  initialValues,
  onSubmit,
  isLoading,
  title,
  submitLabel,
}) {
  const [showFormSelector, setShowFormSelector] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      name: yup
        .string()
        .trim()
        .required("Certification name is required"),
      description: yup.string().nullable(),
      icon_url: yup
        .string()
        .url("Enter a valid URL")
        .nullable(),
      formId: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue === null) return null;
          return Number(value);
        }),
    }),
    onSubmit: values => {
      onSubmit({
        name: values.name.trim(),
        description: values.description?.trim() || null,
        icon_url: values.icon_url?.trim() || null,
        formId:
          values.formId === "" || values.formId === null
            ? null
            : Number(values.formId),
      });
    },
  });

  const handleSelectedForms = forms => {
    if (forms.length > 0) {
      const form = forms[0];
      formik.setFieldValue("formId", form.id);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        backdropClassName="global-backdrop"
        centered
        animation={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.name && !!formik.errors.name}
                placeholder="Certification name"
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.description && !!formik.errors.description
                }
                placeholder="Description (optional)"
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.description}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Form</Form.Label>
              <br />
              <Button
                onClick={() => setShowFormSelector(true)}
                className="mb-2"
                variant="light-blue"
              >
                Select Form
              </Button>

              {formik.values.formId && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span>Selected Form ID: {formik.values.formId}</span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => formik.setFieldValue("formId", "")}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={isLoading}
            >
              {isLoading ? "Please wait..." : submitLabel}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {showFormSelector && (
        <FormSelectorModal
          showchildForm={showFormSelector}
          setShowChildForm={setShowFormSelector}
          handleSelectedForms={handleSelectedForms}
        />
      )}
    </>
  );
}

export default function Certification({ show = true, onHide = () => {} }) {
  const { backendUrl, token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedCertification, setSelectedCertification] = useState(null);

  const initialFilterParams = { q: "", page: 1, limit: 20 };
  const [queryParams, setQueryParams] = useQueryParams(initialFilterParams);
  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });
  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const requestHeaders = useMemo(() => {
    const headers = {
      Accept: "Application/json",
      "Content-Type": "Application/json",
    };

    if (token) headers.Authorization = `Bearer ${token}`;

    return headers;
  }, [token]);

  const getCertifications = async () => {
    let response = await fetch(
      `${backendUrl}${CERTIFICATION_TYPES_ENDPOINT}?${queryString.stringify(
        queryParams
      )}`,
      {
        method: "GET",
        credentials: "include",
        headers: requestHeaders,
      }
    );

    if (!response.ok) {
      const payload = await response.json();
      throw new Error(getErrorMessage(payload));
    }

    const payload = await response.json();
    const data = payload?.data ?? payload ?? {};

    const list = Array.isArray(data)
      ? data
      : data?.certifications || data?.rows || data?.items || data?.data || [];

    const certifications = Array.isArray(list)
      ? list.map(normalizeCertificationItem)
      : [];

    return {
      certifications,
      count:
        payload?.count ||
        data?.count ||
        data?.total ||
        data?.totalCount ||
        certifications.length,
    };
  };

  const {
    data = { certifications: [], count: 0 },
    refetch,
    isFetching,
  } = useQuery([QUERY_KEY, queryParams], getCertifications, {
    enabled: show,
    keepPreviousData: true,
    onError: err => {
      toast.error(err.message || "Unable to fetch certifications");
    },
  });

  const saveCertification = async payload => {
    const endpoint = selectedCertification
      ? `${backendUrl}${CERTIFICATION_TYPES_ENDPOINT}/${
          selectedCertification.id
        }`
      : `${backendUrl}${CERTIFICATION_TYPES_ENDPOINT}`;

    let response = await fetch(endpoint, {
      method: selectedCertification ? "PUT" : "POST",
      credentials: "include",
      headers: requestHeaders,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorPayload = await response.json();
      throw new Error(getErrorMessage(errorPayload));
    }

    return response.json();
  };

  const saveCertificationMutation = useMutation(saveCertification, {
    onSuccess: payload => {
      toast.success(
        payload?.data?.message ||
          payload?.message ||
          (selectedCertification
            ? "Certification updated successfully"
            : "Certification created successfully")
      );
      setShowModal(false);
      setSelectedCertification(null);
      refetch();
    },
    onError: err => {
      toast.error(err.message || "Unable to save certification");
    },
  });

  const deleteCertification = async payload => {
    let response = await fetch(
      `${backendUrl}${CERTIFICATION_TYPES_ENDPOINT}/${payload.id}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: requestHeaders,
      }
    );

    if (!response.ok) {
      const errorPayload = await response.json();
      throw new Error(getErrorMessage(errorPayload));
    }

    return response.json();
  };

  const deleteCertificationMutation = useMutation(deleteCertification, {
    onSuccess: payload => {
      toast.success(
        payload?.data?.message ||
          payload?.message ||
          "Certification deleted successfully"
      );
      refetch();
    },
    onError: err => {
      toast.error(err.message || "Unable to delete certification");
    },
  });

  const handleSearchQueryChange = e => {
    const { name, value } = e.target;
    setFilterParams(prev => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const openCreateModal = () => {
    setSelectedCertification(null);
    setShowModal(true);
  };

  const openEditModal = certification => {
    setSelectedCertification(certification);
    setShowModal(true);
  };

  const handleDelete = async certification => {
    if (
      await ConfirmDialog({
        title: "Delete Certification",
        description: `Are you sure you want to delete ${certification.name}?`,
      })
    ) {
      deleteCertificationMutation.mutate({ id: certification.id });
    }
  };

  const modalInitialValues = useMemo(
    () => ({
      name: selectedCertification?.name || "",
      description: selectedCertification?.description || "",
      icon_url: selectedCertification?.icon_url || "",
      formId: selectedCertification?.formId ?? "",
    }),
    [selectedCertification]
  );

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        scrollable
        backdropClassName="global-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Certification and permits</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <div className="p-0">
            <Card className="border-0 shadow-sm mb-4 rounded-0 ">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between gap-3 w-100">
                  <div className="d-flex flex-wrap gap-3 w-100 justify-content-md-start justify-content-center">
                    <Button variant="primary" onClick={openCreateModal}>
                      + New Certification
                    </Button>

                    <InputGroup
                      style={{ minWidth: "280px", maxWidth: "420px" }}
                    >
                      <InputGroup.Text className="bg-white">
                        <MagnifyIcon />
                      </InputGroup.Text>
                      <Form.Control
                        id="queryParams-q"
                        name="q"
                        value={filterParams.q || ""}
                        onChange={handleSearchQueryChange}
                        placeholder="Search certifications..."
                      />
                    </InputGroup>

                    <Button
                      onClick={() => refetch()}
                      variant="light"
                      className="border text-primary"
                    >
                      <CachedIcon />
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="content px-4 pb-4">
            <Card className="border-0 p-0">
              <Card.Body className="p-0">
                <Table
                  borderless
                  striped
                  responsive
                  className="product-table mb-0"
                >
                  <thead className="sticky border-bottom">
                    <tr>
                      <th>Actions</th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Form ID</th>
                      <th>Created At</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>

                  <tbody className="blue-hover">
                    {data.certifications.map(certification => (
                      <tr key={certification.id}>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant=""
                              className="bg-white border-0"
                              bsPrefix="print more"
                            >
                              <DotsVerticalIcon />
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              popperConfig={{ strategy: "fixed" }}
                              renderOnMount
                              className="dropdown-with-icons"
                            >
                              <Dropdown.Item
                                onClick={() => openEditModal(certification)}
                                as="button"
                              >
                                <EditIcon />
                                Edit
                              </Dropdown.Item>

                              <Dropdown.Item
                                onClick={() => handleDelete(certification)}
                                as="button"
                              >
                                <DeleteIcon />
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{certification.id}</td>
                        <td>{certification.name}</td>
                        <td>{certification.description || "---"}</td>
                        <td>{certification.formId ?? "---"}</td>
                        <td>{formatDate(certification.created_at)}</td>
                        <td>{formatDate(certification.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {data.certifications.length < 1 ? (
                  <div className="d-flex justify-content-center text-center w-100 my-4">
                    <NoTableItem queryParams={queryParams} />
                  </div>
                ) : null}
              </Card.Body>
            </Card>
          </div>

          <div className="d-flex justify-content-between px-4 align-items-center pagination pb-4">
            <div className="pagination_left d-flex gap-3 align-items-center">
              <p className="m-0 p-0">Show</p>
              <select
                value={queryParams.limit}
                name="limit"
                className="form-select"
                onChange={handleSearchQueryChange}
              >
                <option value="10">10 rows</option>
                <option value="20">20 rows</option>
                <option value="30">30 rows</option>
                <option value="40">40 rows</option>
              </select>
            </div>

            <ReactPaginate
              {...paginationOptions}
              pageCount={Math.max(
                1,
                Math.ceil((data.count || 0) / queryParams.limit)
              )}
              marginPagesDisplayed={2}
              pageRangeDisplayed={0}
              onPageChange={({ selected }) => {
                document.body.scrollTop = document.documentElement.scrollTop = 0;
                setQueryParams({
                  ...queryParams,
                  page: selected + 1,
                });
              }}
              forcePage={Math.max(0, Number(queryParams.page || 1) - 1)}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showModal && (
        <CertificationModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setSelectedCertification(null);
          }}
          initialValues={modalInitialValues}
          onSubmit={values => saveCertificationMutation.mutate(values)}
          isLoading={saveCertificationMutation.isLoading}
          title={
            selectedCertification
              ? "Edit Certification"
              : "Create Certification"
          }
          submitLabel={selectedCertification ? "Save Changes" : "Create"}
        />
      )}

      <ModalLoader
        show={
          show &&
          (isFetching ||
            saveCertificationMutation.isLoading ||
            deleteCertificationMutation.isLoading)
        }
      />
    </>
  );
}
